import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { trackEvent, ANALYTICS_EVENTS } from '@/lib/analytics';
import { HelpingHand, MessageSquare, Lightbulb, BarChart3, GraduationCap, ArrowRight } from 'lucide-react';

export default function HelpPage() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row gap-12">

        {/* Main Content Area */}
        <div className="lg:w-3/4">
          <div className="mb-6">
            <h1 className="text-3xl font-bold">Help & Support</h1>
          </div>

          {/* Introduction Section */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Introduction</h2>
            <p className="text-lg">
              Welcome to 8020agent! This application helps you optimize the most important 80/20 of your Google Ads
              <br />
              campaigns by providing insights and recommendations based on your performance data.
            </p>
          </section>

          {/* Getting Started Guide */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Getting Started</h2>
            <ol className="list-decimal list-inside space-y-1 text-lg">
              <li>Create an account or sign in to access the full features of the app.</li>
              <li>Configure your Google Ads integration in the <Link to="/settings" className="text-primary hover:underline">Settings</Link> page.</li>
              <li>Start analyzing your campaign data on the <Link to="/analysis" className="text-primary hover:underline">Analysis</Link> page.</li>
            </ol>
          </section>

          {/* Detailed Page Guides */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Page Guides</h2>

            {/* Analysis Page Guide */}
            <div className="mb-6 p-4 border rounded-lg bg-card">
              <h3 className="text-xl font-semibold mb-2">Analysis Page</h3>
              <p className="text-muted-foreground mb-3">
                Provides detailed insights into your campaign performance. Here, you can:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>View key metrics like impressions, clicks, and conversions.</li>
                <li>Use graphs to visualize performance trends over time.</li>
                <li>Compare current performance with previous periods.</li>
                <li>Receive AI-driven optimization suggestions.</li>
              </ul>
            </div>

            {/* Curve Page Guide */}
            <div className="mb-6 p-4 border rounded-lg bg-card">
              <h3 className="text-xl font-semibold mb-2">Curve Page</h3>
              <p className="text-muted-foreground mb-3">
                Helps you visualize and optimize your profit curves. Features include:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Graphical representation of profit against various metrics.</li>
                <li>Break-even analysis to identify revenue and cost balance points.</li>
                <li>Scenario testing to simulate different outcomes.</li>
              </ul>
            </div>
          </section>

          {/* Common Use Cases */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Common Use Cases</h2>
            <p className="text-lg mb-3">
              Here are some common tasks you can perform with the app:
            </p>
            <ul className="list-disc list-inside text-lg space-y-1">
              <li>Optimize a campaign by adjusting budgets and bids based on analysis insights.</li>
              <li>Analyze performance trends to make data-driven decisions.</li>
              <li>Use the curve page to find the optimal profit point for your campaigns.</li>
            </ul>
          </section>

          {/* Troubleshooting */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold mb-4">Troubleshooting</h2>
            <p className="text-lg">
              If you encounter any issues, visit our{' '}
              <a
                href="https://mikerhodes.circle.so/c/help/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Troubleshooting Forum
              </a>{' '}
              for assistance.
            </p>
          </section>
        </div>

        {/* Sidebar Area */}
        <div className="lg:w-1/4 space-y-6">
          {/* Why Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <HelpingHand className="h-5 w-5 text-primary" />
                Why 8020agent?
              </CardTitle>
              <CardDescription>Understand the vision behind the tool.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" size="sm" asChild className="w-full" onClick={() => trackEvent(ANALYTICS_EVENTS.HELP.STORY_VIEW())}>
                <Link to="/why">Read the Story <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </CardFooter>
          </Card>

          {/* Feedback Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-primary" />
                Give Feedback
              </CardTitle>
              <CardDescription>Help us prioritize features and improve.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="default" size="sm" asChild className="w-full" onClick={() => trackEvent(ANALYTICS_EVENTS.HELP.FEEDBACK_CLICK())}>
                <Link to="/feedback?returnTo=/help">Share Your Thoughts <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </CardFooter>
          </Card>

          {/* Scary Script Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lightbulb className="h-5 w-5 text-primary" />
                Scary Script Challenge
              </CardTitle>
              <CardDescription>Boost your confidence with automation in 14 mins.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" size="sm" asChild className="w-full" onClick={() => trackEvent(ANALYTICS_EVENTS.HELP.CHALLENGE_START())}>
                <Link to="/scary">Start the Challenge <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </CardFooter>
          </Card>

          {/* Profit Curve Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Profit Curve Story
              </CardTitle>
              <CardDescription>Learn interactively how to find your optimal CPA.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" size="sm" asChild className="w-full" onClick={() => trackEvent(ANALYTICS_EVENTS.HELP.CURVE_STORY())}>
                <Link to="/story">Explore the Story <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </CardFooter>
          </Card>

          {/* CPD Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5 text-primary" />
                CPD: Level Up
              </CardTitle>
              <CardDescription>Explore career development opportunities.</CardDescription>
            </CardHeader>
            <CardFooter>
              <Button variant="outline" size="sm" asChild className="w-full">
                <Link to="/CPD">View CPD <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
            </CardFooter>
          </Card>

        </div>
      </div>
    </div>
  );
}